import { useEffect, useRef } from 'react';
import type { OverlayBounds } from '../types';

/**
 * Returns a data URL of the image rotated by the given degrees (clockwise).
 * Used so the floor plan appears "straight" on screen when the map is rotated (map heading).
 * Pass -mapHeading to counter-rotate the image so its top aligns with screen up.
 */
export function rotateImageToDataUrl(imageUrl: string, degrees: number): Promise<string> {
  const normalized = ((degrees % 360) + 360) % 360;
  if (normalized === 0) return Promise.resolve(imageUrl);

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        const rad = (-degrees * Math.PI) / 180;
        const c = Math.abs(Math.cos(rad));
        const s = Math.abs(Math.sin(rad));
        const cw = Math.ceil(w * c + h * s);
        const ch = Math.ceil(w * s + h * c);
        const canvas = document.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(imageUrl);
          return;
        }
        ctx.translate(cw / 2, ch / 2);
        ctx.rotate(rad);
        ctx.drawImage(img, -w / 2, -h / 2, w, h);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(imageUrl);
      }
    };
    img.onerror = () => resolve(imageUrl);
    img.src = imageUrl;
  });
}

/**
 * Ground overlay with zoom-based opacity. Image may be pre-rotated so the floor plan
 * appears straight when map heading is set.
 */
export function useGroundOverlay(
  map: google.maps.Map | null,
  imageUrl: string | null,
  bounds: OverlayBounds | null
): void {
  const overlayRef = useRef<google.maps.GroundOverlay | null>(null);

  useEffect(() => {
    if (!map || !imageUrl || !bounds || typeof google === 'undefined' || !google.maps?.GroundOverlay) return;
    overlayRef.current?.setMap(null);
    overlayRef.current = new google.maps.GroundOverlay(imageUrl, bounds, { clickable: false });
    overlayRef.current.setMap(map);
    return () => {
      overlayRef.current?.setMap(null);
    };
  }, [map, imageUrl, bounds]);

  useEffect(() => {
    if (!map || !overlayRef.current) return;
    const update = () => {
      const z = map.getZoom() ?? 18;
      overlayRef.current?.setOpacity(Math.max(0.2, Math.min(1, (z - 17) / 5)));
    };
    update();
    const l = map.addListener('zoom_changed', update);
    return () => {
      l.remove();
    };
  }, [map]);
}
